import type { Locale } from './locale';

/**
 * Initial-language negotiation for the root URL only (explicit /es/ and /en/ routes always win).
 * Priority: 1. explicit localized URL (never negotiated) · 2. explicit user choice (cx-locale cookie,
 * set only by the ES | EN switch) · 3. Cloudflare country (request.cf.country / CF-IPCountry) ·
 * 4. Accept-Language · 5. English. Auto-detected results are never stored, so a traveller gets the
 * new country's default until they choose a language themselves.
 */
export const SPANISH_COUNTRIES: ReadonlySet<string> = new Set([
  'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'ES', 'GQ', 'GT', 'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'UY', 'VE',
]);
export const LOCALE_COOKIE = 'cx-locale';

export interface LocaleSignals { explicit?: string | null; country?: string | null; acceptLanguage?: string | null }

/** ISO 3166-1 alpha-2 only; Cloudflare's XX (unknown) and T1 (Tor) are treated as unresolved. */
export function countryLocale(country: string | null | undefined): Locale | null {
  const code = country?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(code) || code === 'XX' || code === 'T1') return null;
  return SPANISH_COUNTRIES.has(code) ? 'es-CO' : 'en';
}

/** First Spanish or English language by quality (q) order; null when neither appears. */
export function acceptLanguageLocale(header: string | null | undefined): Locale | null {
  if (!header) return null;
  const ranked = header.split(',').map((part, index) => {
    const [tag, ...params] = part.trim().split(';');
    const q = Number(params.find(param => param.trim().startsWith('q='))?.split('=')[1] ?? 1);
    return { tag: tag.trim().toLowerCase(), q: Number.isFinite(q) ? q : 0, index };
  }).filter(entry => entry.tag && entry.q > 0).sort((a, b) => b.q - a.q || a.index - b.index);
  for (const { tag } of ranked) { if (tag === 'es' || tag.startsWith('es-')) return 'es-CO'; if (tag === 'en' || tag.startsWith('en-')) return 'en'; }
  return null;
}

export function negotiateLocale(signals: LocaleSignals): Locale {
  if (signals.explicit === 'en' || signals.explicit === 'es-CO') return signals.explicit;
  return countryLocale(signals.country) ?? acceptLanguageLocale(signals.acceptLanguage) ?? 'en';
}

export function cookieValue(header: string | null | undefined, name = LOCALE_COOKIE): string | null {
  const match = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`).exec(header ?? '');
  return match ? decodeURIComponent(match[1]) : null;
}
